import React from 'react';
import type { FullLayoutProps } from '@v2/shared/lib/types';

import Content from './Content';
import Footer from './Footer';
import Navigation from './Navigation';

/**
 * The chrome every TopMags page shares.
 *
 * headless: apps/topmags/layouts/MainLayout, ported piece for piece -- the navigation,
 * the content card and the footer are all its components, with next/image, next/router
 * and its config object swapped for the V2 equivalents.
 *
 * The same layout name and the same props as DiscountMags' -- both resolve as
 * `Layouts.Full`, both are fed by FullLayoutPropsBuilder -- and a different view. That
 * is the brand split: a page declares `layoutName = 'Layouts.Full'` and gets its own
 * brand's chrome without knowing which brand it is on.
 *
 * Structurally different rather than recoloured: the navigation is in normal flow
 * instead of fixed, the page is grey, and the content is a white card on it. Nothing
 * here measures a header height, because nothing here is out of flow.
 *
 * The grey starts at md, because the card it exists to define does too -- Content's
 * card is `rounded-none` and full-bleed below md, so on a phone the grey was not framing
 * anything. It was drawn as a border down each side of the header, the content and the
 * footer, which is the one thing a page background should never look like.
 *
 * `displaySitewideBanner` is destructured off deliberately. It is part of the shared
 * props contract and TopMags has no banner to draw, so it is dropped here rather than
 * spread onto Navigation as an unknown attribute.
 */
export default function Full({
  children,
  displaySitewideBanner,
  categories,
  copyrightYear,
}: FullLayoutProps) {
  return (
    <div className="flex flex-col min-h-screen bg-white md:bg-[#F0F0F0]">
      <Navigation categories={categories} />
      <Content>{children}</Content>
      <Footer copyrightYear={copyrightYear} />
    </div>
  );
}
